import { useMemo } from 'react'
import { UsersChipValue } from '~/components/Filters/FilterBar/chips/users'
import type { FilterDefinition } from '~/components/Filters/FilterBar/types'
import { CompaniesAutocomplete } from '~/domains/companies/components/CompaniesAutocomplete'
import { useCompaniesAutocomplete } from '~/domains/companies/queries/companyAutocomplete'
import { InvoiceStatus, InvoiceStatusLabels } from '~/domains/finance/types'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'

const CompanyChipValue = ({ id }: { id: string }) => {
  const { data } = useCompaniesAutocomplete({ ids: [id] })
  const name = data?.pages[0]?.['hydra:member']?.[0]?.name
  return name ?? null
}

export const useInvoiceFilterDefinitions = (): FilterDefinition[] => {
  return useMemo(
    () => [
      {
        key: 'companyId',
        label: 'Entreprise',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <CompaniesAutocomplete
            key={value || 'empty'}
            inlineList
            ids={value ? [value] : []}
            small
            fullWidth
            placeholder="Rechercher une entreprise"
            emptyLabel="Toutes"
            onChange={(v) => {
              onChange(v)
              onClose()
            }}
          />
        ),
        renderValue: (value) => <CompanyChipValue id={value} />
      },
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        options: Object.values(InvoiceStatus).map((status) => ({
          label: InvoiceStatusLabels[status],
          value: status
        })),
        emptyLabel: 'Tous'
      },
      {
        label: "Date d'émission",
        group: 'Dates',
        menuLabel: "Date d'émission",
        type: 'dateRange',
        keyAfter: 'dateFrom',
        keyBefore: 'dateTo'
      },
      {
        label: "Date d'échéance",
        group: 'Dates',
        menuLabel: "Date d'échéance",
        type: 'dateRange',
        keyAfter: 'dueDateFrom',
        keyBefore: 'dueDateTo'
      },
      {
        label: 'Date de règlement',
        group: 'Dates',
        menuLabel: 'Date de règlement',
        type: 'dateRange',
        keyAfter: 'paymentDateFrom',
        keyBefore: 'paymentDateTo'
      },
      {
        key: 'userIds',
        label: 'Utilisateurs',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <UsersAutocomplete
            key={value || 'empty'}
            multiple
            inlineList
            ids={value ? value.split(',').filter(Boolean) : []}
            listingContext="orders"
            small
            fullWidth
            placeholder="Rechercher des utilisateurs"
            onChange={(v) => {
              onChange(v.join(','))
              onClose()
            }}
          />
        ),
        renderValue: (value) => <UsersChipValue value={value} />
      },
      {
        key: 'label',
        label: 'Libellé',
        type: 'text',
        placeholder: 'Rechercher par libellé…'
      },
      {
        key: 'externalRef',
        label: 'Réf. externe',
        type: 'text',
        placeholder: 'Rechercher par réf. externe…'
      },
      {
        key: 'id',
        label: 'N° facture',
        type: 'text',
        placeholder: 'Numéro exact…',
        renderValue: (value) => `#${value}`,
        transform: (value) => value.replace(/\D/g, '')
      }
    ],
    []
  )
}
